import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { TrustBar } from "@/components/TrustBar";
import { OffersGrid } from "@/components/OffersGrid";
import { WhyBroker } from "@/components/WhyBroker";
import { HowItWorks } from "@/components/HowItWorks";
import { WhatsIncluded } from "@/components/WhatsIncluded";
import { ConsultantCard } from "@/components/ConsultantCard";
import { PreventivoSection } from "@/components/PreventivoSection";
import { Faq } from "@/components/Faq";
import { CtaFinal } from "@/components/CtaFinal";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useSeo, faqSchema } from "@/lib/seo";
import { FAQ_AZIENDE, RACING_IMAGES, waLink } from "@/data/content";

const HERO_PHOTO = "https://images.unsplash.com/photo-1699325974549-fd06639650aa?crop=entropy&cs=srgb&fm=jpg&q=85";

const FISCALI_AZIENDE = [
  { profilo: "Auto in uso promiscuo ai dipendenti", deduzione: "70%", limite: "nessun limite di importo", iva: "40%" },
  { profilo: "Veicoli esclusivamente strumentali", deduzione: "100%", limite: "nessun limite di importo", iva: "100%" },
  { profilo: "Auto aziendali non assegnate", deduzione: "20%", limite: "max € 3.615,20 / anno", iva: "40%" },
];

const TaxBenefitsAziende = () => (
  <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20" data-testid="tax-benefits">
    <Reveal>
      <p className="text-xs font-semibold uppercase tracking-widest text-xmove-orange">Vantaggi fiscali</p>
      <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold text-xmove-ink sm:text-4xl">
        Quanto scarica la tua azienda dal canone
      </h2>
      <p className="mt-3 max-w-2xl text-base text-xmove-muted md:text-lg">
        Art. 164 TUIR applicato alla tua flotta: le tre casistiche che contano per una società.
      </p>
    </Reveal>
    <Reveal delay={0.1}>
      <div className="mt-10 overflow-hidden rounded-2xl border border-xmove-ink/8 bg-white shadow-soft">
        <div className="hidden grid-cols-4 gap-4 border-b border-xmove-ink/8 bg-xmove-cream px-6 py-4 text-xs font-bold uppercase tracking-wider text-xmove-muted md:grid">
          <span>Casistica</span><span>Costo deducibile</span><span>Limite annuo</span><span>IVA detraibile</span>
        </div>
        {FISCALI_AZIENDE.map((r) => (
          <div key={r.profilo} className="grid grid-cols-2 gap-x-4 gap-y-2 border-b border-xmove-ink/6 px-6 py-5 last:border-0 md:grid-cols-4 md:items-center">
            <span className="col-span-2 text-sm font-bold text-xmove-ink md:col-span-1">{r.profilo}</span>
            <span className="text-sm text-xmove-ink"><span className="text-xs text-xmove-muted md:hidden">Deduzione: </span><b className="text-xmove-orange">{r.deduzione}</b></span>
            <span className="text-sm text-xmove-muted">{r.limite}</span>
            <span className="col-span-2 text-sm md:col-span-1"><span className="text-xs text-xmove-muted md:hidden">IVA: </span><b className="text-xmove-green">{r.iva}</b></span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-xmove-muted" data-testid="tax-disclaimer">*Tabella puramente indicativa: consultare sempre il proprio commercialista.</p>
    </Reveal>
    <Reveal delay={0.15}>
      <Link
        to="/guide/deducibilita-noleggio-auto-partita-iva/"
        data-testid="aziende-guide-link"
        className="mt-6 inline-flex items-center gap-2 font-display text-sm font-bold text-xmove-orange transition-colors hover:text-xmove-orangeDark"
      >
        Approfondisci con la guida fiscale completa <ArrowRight size={16} />
      </Link>
    </Reveal>
  </section>
);

const AziendeSeoContent = () => (
  <section className="mx-auto max-w-3xl px-5 py-16 lg:px-8" data-testid="aziende-seo-content">
    <Reveal>
      <h2 className="font-display text-2xl font-bold text-xmove-ink sm:text-3xl">
        Noleggio lungo termine per aziende: la mobilità esce dal bilancio ed entra in una fattura
      </h2>
      <div className="mt-6 space-y-5 text-base leading-relaxed text-xmove-muted">
        <p>
          Per una società, ogni auto di proprietà è un cespite da ammortizzare, un'assicurazione da
          rinnovare, un sinistro da gestire e un usato da rivendere. Il noleggio a lungo termine elimina
          tutto questo: il veicolo diventa un servizio a canone fisso, con una fattura mensile unica e
          nessuna immobilizzazione di capitale né impatto sull'affidamento bancario.
        </p>
        <h3 className="font-display text-xl font-bold text-xmove-ink">Flotte gestite, non subite</h3>
        <p>
          Che siano 2 auto o 20, con XMOVE hai un unico referente per l'intero parco: rinnovi scaglionati,
          sostituzioni programmate, gestione sinistri centralizzata e reportistica sui costi. La vostra
          amministrazione smette di rincorrere scadenze e polizze: arriva una fattura, puntuale, per
          ogni veicolo.
        </p>
        <h3 className="font-display text-xl font-bold text-xmove-ink">Uso promiscuo ai dipendenti: il 70% che molti dimenticano</h3>
        <p>
          Assegnare l'auto in uso promiscuo ai dipendenti per la maggior parte del periodo d'imposta porta
          la deducibilità al 70% senza limiti di importo: spesso è la leva fiscale più potente per una
          società. Prepariamo noi la documentazione di assegnazione e il prospetto per il consulente del
          lavoro sul fringe benefit.
        </p>
        <h3 className="font-display text-xl font-bold text-xmove-ink">Perché una società sceglie XMOVE</h3>
        <p>
          Confrontare listini e disponibilità richiede tempo e competenze di settore. XMOVE lo fa per te:
          consultiamo lo stock più ampio d'Italia su ogni veicolo della flotta, negoziamo le condizioni
          complessive e ti presentiamo un quadro chiaro. Tu decidi, noi eseguiamo.
        </p>
      </div>
    </Reveal>
  </section>
);

export default function Aziende() {
  useSeo({
    title: "Noleggio Lungo Termine Aziende: Offerte {Mese Anno} | XMOVE",
    description:
      "Noleggio lungo termine per aziende e SRL: canone fisso tutto incluso, gestione flotte, uso promiscuo dipendenti al 70% e un unico referente. Preventivo gratuito.",
    path: "/noleggio-lungo-termine-aziende/",
    jsonLd: [faqSchema(FAQ_AZIENDE)],
  });

  return (
    <div className="pb-[72px] md:pb-0" data-testid="aziende-page">
      <Header />
      <main>
        <Hero
          eyebrow="Dedicato ad Aziende, SRL e Flotte"
          h1Lines={["Noleggio Lungo Termine", "per Aziende"]}
          subtitle="Dalla singola auto direzionale all'intera flotta: canone fisso tutto incluso, gestione sinistri centralizzata e un unico referente per tutto il parco auto. Selezioniamo l'offerta migliore dallo stock più ampio d'Italia."
          photo={HERO_PHOTO}
          photoAlt="Auto aziendale premium a noleggio lungo termine"
        />
        <Marquee />
        <TrustBar />
        <TaxBenefitsAziende />
        <div className="bg-white">
          <OffersGrid
            target="aziende"
            title="Offerte business per la tua azienda"
            subtitle="Modelli direzionali e da flotta con canoni negoziati e condizioni multi-veicolo disponibili."
          />
        </div>
        <WhyBroker />
        <HowItWorks />
        <WhatsIncluded dark />
        <AziendeSeoContent />
        <ConsultantCard racingBg={RACING_IMAGES[0]} />
        <PreventivoSection
          idPrefix="aziende"
          title="Preventivo per la tua azienda"
          subtitle="Dalla singola auto alla flotta completa: un consulente dedicato ti richiama in pochi minuti."
        />
        <Faq faqs={FAQ_AZIENDE} title="Domande frequenti — Aziende" />
        <CtaFinal
          title="La tua flotta, gestita come un pit stop"
          subtitle="Raccontaci quante auto vi servono: ti richiamiamo in pochi minuti con la selezione delle migliori offerte."
        />
      </main>
      <Footer />
      <MobileStickyBar waText={waLink("Salve XMOVE, vi contatto per la mia azienda: vorrei un preventivo per il noleggio lungo termine.")} />
    </div>
  );
}
